import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Measuring Financial Well-Being',
    content: [
      '<strong>Learning Goal:</strong> Accurately interpret IFDFW results, identify areas of strength and improvement in financial management, and link perception of control to practical money behaviors.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• How to administer and score the IFDFW assessment',
      '• Understanding the scoring categories and what they mean',
      '• Interpreting your personal results',
      '• Using assessment results for financial planning',
      '<br/>',
      '<strong>Discussion:</strong> The IFDFW scale provides a comprehensive snapshot of your financial well-being, helping you understand both your current state and areas for growth.'
    ]
  },
  {
    title: 'Taking the IFDFW Assessment',
    content: [
      'The InCharge Financial Distress/Financial Well-Being Scale consists of 8 statements that you rate on a 5-point scale.',
      '<br/>',
      '<strong>How to Take the Assessment:</strong>',
      '1. <strong>Find a quiet space</strong> where you can reflect honestly',
      '2. <strong>Read each statement</strong> carefully',
      '3. <strong>Rate your agreement</strong> on a scale of 1-5:',
      '   • 1 = Strongly Disagree',
      '   • 2 = Disagree',
      '   • 3 = Neither Agree nor Disagree',
      '   • 4 = Agree',
      '   • 5 = Strongly Agree',
      '4. <strong>Answer honestly</strong> about how you currently feel',
      '<br/>',
      '<strong>The 8 IFDFW Statements:</strong>',
      '1. I worry about meeting monthly expenses',
      '2. I could handle an unexpected $1,000 expense',
      '3. I live paycheck to paycheck',
      '4. I am satisfied with my current financial situation',
      '5. I feel in control of my finances',
      '6. I worry about my financial future',
      '7. My finances cause me a lot of stress',
      '8. I have a positive financial outlook',
      '<br/>',
      '<strong>Important Note:</strong> There are no "right" or "wrong" answers. This assessment measures your subjective experience of financial well-being.'
    ]
  },
  {
    title: 'Calculating Your IFDFW Score',
    content: [
      'Scoring the IFDFW is straightforward but requires careful attention to positive and negative statements.',
      '<br/>',
      '<strong>Step-by-Step Scoring:</strong>',
      '1. <strong>Add up all 8 ratings</strong> (each 1-5)',
      '2. <strong>Check your total score</strong> (ranges from 8-40)',
      '3. <strong>Compare to categories</strong> below',
      '<br/>',
      '<strong>Important:</strong> Statements 1, 3, 6, and 7 are negative (higher numbers = more distress). The assessment automatically accounts for this in the total score.',
      '<br/>',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header pillar-cell">SCORE RANGE</div>
          <div class="table-header means-cell">CATEGORY</div>
          <div class="table-header matters-cell">DESCRIPTION</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">8-29</div>
          <div class="table-cell means-cell">High Financial Distress</div>
          <div class="table-cell matters-cell">Frequent worry, limited control, possible financial crisis</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">30-49</div>
          <div class="table-cell means-cell">Moderate Distress</div>
          <div class="table-cell matters-cell">Basic stability but recurring financial strain</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">50-80</div>
          <div class="table-cell means-cell">High Financial Well-Being</div>
          <div class="table-cell matters-cell">Secure, confident, and satisfied with financial management</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Key Insight:</strong> Your score indicates your current level of financial well-being, not your financial intelligence or worth as a person.'
    ]
  },
  {
    title: 'Interpreting Your Results',
    content: [
      'Understanding what your IFDFW score means helps you use it as a tool for financial growth rather than judgment.',
      '<br/>',
      '<strong>High Financial Distress (8-29):</strong>',
      '• <strong>Common experiences:</strong> Constant worry about money, feeling overwhelmed by expenses',
      '• <strong>Emotional impact:</strong> High anxiety, difficulty sleeping, strained relationships',
      '• <strong>Next steps:</strong> Focus on immediate relief strategies and basic financial education',
      '<br/>',
      '<strong>Moderate Distress (30-49):</strong>',
      '• <strong>Common experiences:</strong> Occasional financial worries, some stability but ongoing stress',
      '• <strong>Emotional impact:</strong> Periodic anxiety, some decision fatigue',
      '• <strong>Next steps:</strong> Build emergency savings and improve financial literacy',
      '<br/>',
      '<strong>High Financial Well-Being (50-80):</strong>',
      '• <strong>Common experiences:</strong> Financial security, confidence in decision-making',
      '• <strong>Emotional impact:</strong> Low stress, positive outlook, energy for other life goals',
      '• <strong>Next steps:</strong> Maintain habits and help others improve their financial well-being',
      '<br/>',
      '<strong>Remember:</strong> Your score is a snapshot, not a permanent label. Financial well-being can improve with intentional effort.'
    ]
  },
  {
    title: 'Using Assessment Results for Growth',
    content: [
      'The IFDFW assessment is most valuable when used as a starting point for positive change, not as a final judgment.',
      '<br/>',
      '<strong>Identify Strengths:</strong>',
      '• Which dimensions scored highest? These are your financial strengths',
      '• What positive financial habits do you already have?',
      '• How can you leverage these strengths in other areas?',
      '<br/>',
      '<strong>Target Improvements:</strong>',
      '• Which dimensions scored lowest? These need attention',
      '• What specific actions can address these areas?',
      '• Are there underlying beliefs or habits contributing to low scores?',
      '<br/>',
      '<strong>Link to Behaviors:</strong>',
      '• <strong>Low emergency confidence:</strong> Build emergency savings',
      '• <strong>High expense worry:</strong> Create and follow a budget',
      '• <strong>Paycheck-to-paycheck:</strong> Increase income or reduce expenses',
      '• <strong>Low control:</strong> Learn basic financial skills and track progress',
      '<br/>',
      '<strong>Takeaway:</strong> Your IFDFW results are a roadmap for financial growth, not a measure of your value or intelligence.'
    ],
    additionalSection: {
      title: 'Personal Financial Snapshot Activity',
      content: [
        'After taking the IFDFW assessment, create your personal financial snapshot:',
        '<br/>',
        '<strong>Reflection Questions:</strong>',
        '• What surprised you about your score?',
        '• What strengths did you discover?',
        '• What areas need the most attention?',
        '<br/>',
        '<strong>Action Planning:</strong>',
        '• What\'s one behavior you could change this month to reduce financial stress?',
        '• Who could support you in making this change?',
        '• How will you track your progress?',
        '<br/>',
        '<strong>Weekly Check-in:</strong> Revisit these questions weekly to monitor your financial well-being journey.',
        '<br/>',
        '<strong>Reflection:</strong> The IFDFW assessment is a tool for self-awareness and growth, not self-judgment.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 3: Measuring Financial Well-Being! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about the IFDFW assessment and scoring.',
        'The quiz will help you review key concepts about measuring financial well-being.'
      ]
    }
  }
];

const FinancialModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('financialM3');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/financial-module-3/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/financial-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left financial-panel"
        >
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">Measuring Financial Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Accurately interpret IFDFW results, identify areas of strength and improvement in financial management, and link perception of control to practical money behaviors.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/financial-module-3/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule3Page;

